import { findRendererApp } from "./renderer_app"

/**
 * Whether this machine is a budget one, for rendering choices that trade memory or fill rate for
 * quality (the shadows' bake resolution). Decided once, from what the browser will say:
 *  - the GPU WebGL reports: a software renderer, or an integrated or mobile GPU (Intel, Mali, Adreno,
 *    PowerVR, Apple's phone and tablet GPUs), which shares the machine's RAM;
 *  - the machine's memory (navigator.deviceMemory, Chromium only) and cores, where given;
 *  - the largest texture the GPU takes.
 * Any one of them marks it budget. Override live with window.graphicsTier = "budget" | "full" (it is read
 * every time, so set it before the shadows are next drawn) -- also how to try either tier on any machine.
 */
export type DeviceTier = "budget" | "full"

export interface DeviceSignals {
    gpu?: string
    memoryGb?: number
    cores?: number
    maxTextureSize?: number
    mobile?: boolean
}

const BUDGET_GPU = /swiftshader|llvmpipe|software|microsoft basic|intel|mali|adreno|powervr|apple gpu|videocore|tegra/i

/** The tier these signals point to. Pure, for tests. */
export const deviceTierFrom = ({ gpu, memoryGb, cores, maxTextureSize, mobile }: DeviceSignals): DeviceTier => {
    if (mobile) return "budget"
    if (gpu && BUDGET_GPU.test(gpu)) return "budget"
    if (memoryGb !== undefined && memoryGb <= 4) return "budget"
    if (cores !== undefined && cores <= 4) return "budget"
    if (maxTextureSize !== undefined && maxTextureSize < 8192) return "budget"
    return "full"
}

const readSignals = (): DeviceSignals | undefined => {
    if (typeof navigator === "undefined") return undefined
    const gl = (findRendererApp()?.renderer as any)?.gl as WebGLRenderingContext | undefined
    if (!gl) return undefined
    let gpu: string | undefined
    try {
        const info = gl.getExtension("WEBGL_debug_renderer_info")
        gpu = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? "")
    } catch {
        gpu = undefined
    }
    const nav = navigator as any
    return {
        gpu,
        memoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : undefined,
        cores: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : undefined,
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        mobile: /iphone|ipad|ipod|android/i.test(nav.userAgent ?? "") || (nav.maxTouchPoints > 1 && /macintosh/i.test(nav.userAgent ?? "")),
    }
}

let detected: { tier: DeviceTier; signals: DeviceSignals } | undefined

/** This machine's tier; "full" until a renderer exists to ask (headless tests). */
export const deviceTier = (): DeviceTier => {
    const w = typeof window === "undefined" ? undefined : (window as any)
    if (w?.graphicsTier === "budget" || w?.graphicsTier === "full") return w.graphicsTier
    if (!detected) {
        const signals = readSignals()
        if (!signals) return "full"
        detected = { tier: deviceTierFrom(signals), signals }
        if (w) w.deviceTierSignals = { ...signals, tier: detected.tier }
    }
    return detected.tier
}

/** Pixels per world pixel for the shadows' bakes: soft art loses little at half, and costs a quarter. */
export const shadowBakeResolution = (): number => (deviceTier() === "budget" ? 0.5 : 1)
